import { Injectable, signal, computed, inject } from '@angular/core';
import { ServerService } from './server.service';
import { Subject } from 'rxjs';
import { BattleChatMessage, ChatMessage } from '../types/chat';
import { Hex } from 'viem';
import { Wallet } from './wallet';
import { OnchainStateService } from './onchain-state.service';
import { addressEquals } from './utils.service';
import { ZERO_BYTES_32 } from '../types/constants';
import {
  BattleLogEvent,
  ChatMessageResponsePayload,
  RequestType,
} from '../types/server';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private static readonly LOBBY_FEED_CAP = 300;

  private server = inject(ServerService);
  private wallet = inject(Wallet);
  private state = inject(OnchainStateService);

  // Per-battle chat, keyed battleKey → messageId. Keying by the server id makes
  // a re-delivered message (history resend) collapse onto its slot rather than
  // duplicate.
  logHistory: Map<Hex, Map<string, BattleChatMessage>> = new Map();
  chatMessageEntry: Subject<BattleChatMessage> =
    new Subject<BattleChatMessage>();

  // Lobby feed source of truth, keyed by a stable per-entry id: the server id
  // for chat, bl:<eventId> for battle-log, uuid for system. Keying makes every
  // insert idempotent — reconnect re-auth resends the whole chat history, and
  // the resend lands on existing ids instead of piling up duplicates. The
  // public list is the id map, time-sorted.
  private readonly lobbyById = signal<Map<string, ChatMessage & { id: string }>>(
    new Map()
  );
  readonly lobbyLogMessages = computed(() =>
    [...this.lobbyById().values()].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    )
  );

  // Game events only — the deck's ticker shows these; chat stays in the log.
  readonly lobbyGameEvents = computed(() =>
    this.lobbyLogMessages().filter(
      (m) => m.type !== 'YOU_CHAT' && m.type !== 'OTHERS_CHAT'
    )
  );

  // BATTLE_START and BATTLE_COMPLETE are independent, unlinked feed entries (keyed by their
  // own server-assigned id) - this is the only join key a Spectate button has to hide itself.
  readonly completedBattleKeys = computed<ReadonlySet<Hex>>(() => {
    const keys = new Set<Hex>();
    for (const m of this.lobbyLogMessages()) {
      const ev = m.battleLogEvent;
      if (ev?.type === 'BATTLE_COMPLETE') keys.add(ev.battleKey);
    }
    return keys;
  });

  sendLobbyMessage(message: string): void {
    this.server.queueMessage({
      type: RequestType.CHAT_MSG,
      payload: { message, battleKey: ZERO_BYTES_32 },
    });
  }

  constructor() {
    // Lobby chat history arrives as part of the ADDRESS_AUTH snapshot, so the
    // service doesn't request it — that previously raced auth and got dropped.

    this.server.battleLogSnapshot.subscribe((events) => {
      for (const ev of events) this.appendBattleLogEvent(ev);
    });
    this.server.battleLogEvent.subscribe((ev) => this.appendBattleLogEvent(ev));

    this.server.chatMessage.subscribe((msg) => {
      if (msg.battleKey.toLowerCase() === ZERO_BYTES_32) {
        this.handleLobbyChatMessage(msg);
      } else {
        this.handleBattleChatMessage(msg);
      }
    });

  }

  // Insert unless the id is already present; a repeat id is a re-delivery.
  // Callers supply the id separately, so the payload omits it.
  private upsertLobbyMessage(id: string, message: Omit<ChatMessage, 'id'>): void {
    this.lobbyById.update((prev) => {
      if (prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, { ...message, id });
      // Cap the retained feed; streams can interleave out of timestamp order,
      // so evict the oldest by timestamp rather than insertion order.
      if (next.size > ChatService.LOBBY_FEED_CAP) {
        let oldestId: string | undefined;
        let oldestTs = Infinity;
        for (const [key, m] of next) {
          const ts = m.timestamp.getTime();
          if (ts < oldestTs) {
            oldestTs = ts;
            oldestId = key;
          }
        }
        if (oldestId !== undefined) next.delete(oldestId);
      }
      return next;
    });
  }

  private appendBattleLogEvent(event: BattleLogEvent) {
    this.upsertLobbyMessage(`bl:${event.id}`, {
      msg: '',
      type: 'BATTLE_LOG',
      battleLogEvent: event,
      timestamp: new Date(event.timestamp),
    });
  }

  private handleLobbyChatMessage(msg: ChatMessageResponsePayload) {
    const isSelf = this.isSelfAddress(msg.address);
    const usernameForAddress = this.resolveChatDisplayName(msg, isSelf);
    this.upsertLobbyMessage(msg.id, {
      msg: `${usernameForAddress}: ${msg.message}`,
      username: usernameForAddress,
      body: msg.message,
      type: isSelf ? 'YOU_CHAT' : 'OTHERS_CHAT',
      address: msg.address,
      timestamp: new Date(msg.timestamp),
    });
  }

  private handleBattleChatMessage(msg: ChatMessageResponsePayload) {
    const existing = this.logHistory.get(msg.battleKey);
    if (existing?.has(msg.id)) return; // re-delivered — already shown

    const isSelf = this.isSelfAddress(msg.address);
    const usernameForAddress = this.resolveChatDisplayName(msg, isSelf);
    const chatMessage: BattleChatMessage = {
      id: msg.id,
      msg: `${usernameForAddress}: ` + msg.message,
      type: isSelf ? 'YOU_CHAT' : 'OTHERS_CHAT',
      battleKey: msg.battleKey,
      fromAddress: msg.address,
      timestamp: new Date(msg.timestamp),
    };

    const byId = existing ?? new Map<string, BattleChatMessage>();
    byId.set(msg.id, chatMessage);
    this.logHistory.set(msg.battleKey, byId);
    this.chatMessageEntry.next(chatMessage);
  }

  private isSelfAddress(address?: Hex): boolean {
    return addressEquals(address, this.wallet.activeAddress());
  }

  // Prefer a linked X handle over the username; resolveIdentity fills any gap
  // from the cached client list when the server echo omits it. Self falls back
  // to the local wallet username, everyone else to the raw address.
  private resolveChatDisplayName(
    msg: { username: string; twitterHandle: string | null; address?: Hex },
    isSelf: boolean
  ): string {
    const { username, twitterHandle } = this.server.resolveIdentity(msg);
    if (twitterHandle) return `@${twitterHandle}`;
    if (username) return username;
    if (isSelf) return this.wallet.username() || 'anon';
    return msg.address?.toLowerCase() || 'anon';
  }
}
